import React, { useEffect, useState } from 'react';
import { Horse, useHorse } from '../../contexts/HorseContext';
import { ScaleIcon, InfoIcon, RefreshCwIcon, BarChartIcon, DatabaseIcon } from 'lucide-react';
import { CountdownModal } from './CountdownModal';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
interface HorseDetailsProps {
  horse: Horse;
}
export const HorseDetails: React.FC<HorseDetailsProps> = ({
  horse
}) => {
  const {
    addWeight,
    horses
  } = useHorse();
  const [weight, setWeight] = useState('');
  const [isWeighing, setIsWeighing] = useState(false);
  const [showCountdown, setShowCountdown] = useState(false);
  const [lastAddedWeight, setLastAddedWeight] = useState<number | null>(null);
  const [isZeroing, setIsZeroing] = useState(false);
  const [activeTab, setActiveTab] = useState<'data' | 'statistics'>('data');
  // Get the current horse data directly from context to ensure it's always up-to-date
  const currentHorse = horses.find(h => h.id === horse.id) || horse;
  const handleWeighingStart = () => {
    setShowCountdown(true);
  };
  const handleZeroScale = () => {
    setIsZeroing(true);
    // Simulate the zeroing process with a timeout
    setTimeout(() => {
      setIsZeroing(false);
      // Show a success message
      alert('Waage wurde erfolgreich auf Null gestellt');
    }, 1500);
  };
  const handleCountdownComplete = (detectedWeight?: number) => {
    setShowCountdown(false);
    if (detectedWeight) {
      // Automatically save the detected weight
      addWeight(horse.id, detectedWeight);
      setLastAddedWeight(detectedWeight);
    } else {
      // Show manual input form if no weight was detected/accepted
      setIsWeighing(true);
    }
  };
  const handleWeighingSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!weight) {
      alert('Bitte geben Sie ein Gewicht ein');
      return;
    }
    const weightValue = parseFloat(weight);
    if (isNaN(weightValue) || weightValue <= 0) {
      alert('Bitte geben Sie ein gültiges Gewicht ein');
      return;
    }
    addWeight(horse.id, weightValue);
    setLastAddedWeight(weightValue);
    setWeight('');
    setIsWeighing(false);
  };
  // Sort weights by date (newest first)
  const sortedWeights = [...currentHorse.weights].sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
  // Sort weights by date (oldest first) for the chart
  const chartData = [...currentHorse.weights].sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime()).map(entry => ({
    date: new Date(entry.date).toLocaleDateString('de-DE'),
    weight: entry.weight
  }));
  // Get the latest weight to display
  const latestWeight = sortedWeights.length > 0 ? sortedWeights[0] : null;
  return <div className="bg-white rounded-lg shadow p-6 h-full flex flex-col" data-id="element-250">
      {/* Tab Navigation */}
      <div className="flex border-b mb-4" data-id="element-251">
        <button className={`px-4 py-2 font-medium flex items-center ${activeTab === 'data' ? 'text-[#96c11f] border-b-2 border-[#96c11f]' : 'text-gray-500 hover:text-gray-700'}`} onClick={() => setActiveTab('data')} data-id="element-252">
          <DatabaseIcon size={18} className="mr-2" data-id="element-253" />
          Pferdedaten
        </button>
        <button className={`px-4 py-2 font-medium flex items-center ${activeTab === 'statistics' ? 'text-[#96c11f] border-b-2 border-[#96c11f]' : 'text-gray-500 hover:text-gray-700'}`} onClick={() => setActiveTab('statistics')} data-id="element-254">
          <BarChartIcon size={18} className="mr-2" data-id="element-255" />
          Statistik
        </button>
      </div>
      {activeTab === 'data' /* Horse Data Tab Content */ ? <div className="mb-6 flex-shrink-0" data-id="element-256">
          <div className="grid grid-cols-2 gap-3 mb-4" data-id="element-257">
            <div data-id="element-258">
              <p className="text-sm text-gray-500" data-id="element-259">Name</p>
              <p className="font-medium" data-id="element-260">{currentHorse.name}</p>
            </div>
            <div data-id="element-261">
              <p className="text-sm text-gray-500" data-id="element-262">Geburtsjahr</p>
              <p className="font-medium" data-id="element-263">{currentHorse.birthYear}</p>
            </div>
            <div data-id="element-264">
              <p className="text-sm text-gray-500" data-id="element-265">Rasse</p>
              <p className="font-medium" data-id="element-266">{currentHorse.breed || '-'}</p>
            </div>
            <div data-id="element-267">
              <p className="text-sm text-gray-500" data-id="element-268">Abstammung</p>
              <p className="font-medium" data-id="element-269">{currentHorse.lineage || '-'}</p>
            </div>
          </div>
          {latestWeight && <div className="mb-4" data-id="element-270">
              <p className="text-sm text-gray-500 mb-1" data-id="element-271">Letzte Verwiegung</p>
              <p className="font-medium text-lg bg-[#96c11f]/10 p-2 rounded-md border-l-4 border-[#96c11f]" data-id="element-272">
                {latestWeight.weight} kg
                <span className="ml-2 text-sm text-gray-500" data-id="element-273">
                  ({new Date(latestWeight.date).toLocaleDateString('de-DE')})
                </span>
              </p>
            </div>}
          {isWeighing ? <form onSubmit={handleWeighingSubmit} className="mt-4" data-id="element-274">
              <div className="mb-3" data-id="element-275">
                <label htmlFor="weight" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-276">
                  Gewicht (kg)
                </label>
                <input type="number" id="weight" value={weight} onChange={e => setWeight(e.target.value)} className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#96c11f] focus:border-[#96c11f] focus:bg-[#96c11f]/5" step="0.1" min="0" required data-id="element-277" />
              </div>
              <div className="flex gap-2" data-id="element-278">
                <button type="button" onClick={() => setIsWeighing(false)} className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-[#96c11f] hover:text-white hover:border-[#96c11f]" data-id="element-279">
                  Abbrechen
                </button>
                <button type="submit" className="px-4 py-2 bg-[#284755] text-white rounded-md flex items-center hover:bg-[#96c11f]" data-id="element-280">
                  <ScaleIcon size={18} className="mr-2" data-id="element-281" />
                  Gewicht speichern
                </button>
              </div>
            </form> : <div className="flex gap-2" data-id="element-282">
              <button onClick={handleWeighingStart} className="flex-1 px-4 py-3 bg-[#284755] text-white rounded-md flex items-center justify-center hover:bg-[#96c11f]" data-id="element-283">
                <ScaleIcon size={20} className="mr-2" data-id="element-284" />
                Wiegen
              </button>
              <button onClick={handleZeroScale} disabled={isZeroing} className="px-4 py-3 bg-gray-100 text-gray-800 border border-gray-300 rounded-md flex items-center justify-center hover:bg-[#96c11f] hover:text-white hover:border-[#96c11f] transition-colors" data-id="element-285">
                <RefreshCwIcon size={20} className={`mr-2 ${isZeroing ? 'animate-spin' : ''}`} data-id="element-286" />
                Waage nullstellen
              </button>
            </div>}
        </div> /* Statistics Tab Content */ : <div className="mb-6 flex-shrink-0" data-id="element-287">
          <h3 className="text-lg font-medium mb-3" data-id="element-288">Gewichtsverlauf</h3>
          {chartData.length > 0 ? <div className="h-64 w-full mb-4" data-id="element-289">
              <ResponsiveContainer width="100%" height="100%" data-id="element-290">
                <LineChart data={chartData} margin={{
            top: 5,
            right: 20,
            left: 10,
            bottom: 5
          }} data-id="element-291">
                  <CartesianGrid strokeDasharray="3 3" data-id="element-292" />
                  <XAxis dataKey="date" data-id="element-293" />
                  <YAxis domain={['dataMin - 20', 'dataMax + 20']} label={{
              value: 'kg',
              angle: -90,
              position: 'insideLeft'
            }} data-id="element-294" />
                  <Tooltip formatter={value => [`${value} kg`, 'Gewicht']} data-id="element-295" />
                  <Line type="monotone" dataKey="weight" stroke="#96c11f" strokeWidth={2} dot={{
              r: 5
            }} activeDot={{
              r: 8
            }} data-id="element-296" />
                </LineChart>
              </ResponsiveContainer>
            </div> : <p className="text-sm text-gray-500 italic mb-4" data-id="element-297">
              Keine Gewichtsdaten vorhanden. Führen Sie eine Verwiegung durch,
              um Daten zu sammeln.
            </p>}
        </div>}
      {/* Marks/Injuries Section - Fixed height with scrolling */}
      <div className="mt-4" data-id="element-298">
        <h3 className="text-lg font-medium mb-2 flex items-center" data-id="element-299">
          <InfoIcon size={18} className="mr-1 text-amber-500" data-id="element-300" />
          Merkmale & Verletzungen
        </h3>
        <div className="h-48 overflow-y-auto border border-gray-100 rounded-md" data-id="element-301">
          {currentHorse.marks && currentHorse.marks.length > 0 ? <ul className="space-y-2 p-2" data-id="element-302">
              {currentHorse.marks.map(mark => <li key={mark.id} className="bg-gray-50 p-3 rounded-md text-sm cursor-pointer hover:bg-gray-100 transition-colors" onClick={() => {
            // Emit an event or update state to highlight this mark on the horse image
            const markElement = document.querySelector(`[data-mark-id="${mark.id}"]`);
            if (markElement) {
              // Remove highlight from all marks
              document.querySelectorAll('[data-mark-id]').forEach(el => {
                el.classList.remove('bg-blue-500');
                el.classList.remove('animate-pulse');
                el.classList.add('bg-red-500');
              });
              // Highlight this mark
              markElement.classList.remove('bg-red-500');
              markElement.classList.add('bg-blue-500');
              markElement.classList.add('animate-pulse');
              // Scroll to mark if needed
              markElement.scrollIntoView({
                behavior: 'smooth',
                block: 'center'
              });
            }
          }} data-id="element-303">
                  <div className="font-medium" data-id="element-304">{mark.description}</div>
                  <div className="text-gray-500 text-xs" data-id="element-305">{mark.date}</div>
                </li>)}
            </ul> : <p className="text-sm text-gray-500 italic p-4" data-id="element-306">
              Keine Merkmale oder Verletzungen vorhanden. Klicken Sie auf das
              Pferdebild, um Merkmale hinzuzufügen.
            </p>}
        </div>
      </div>
      {/* Countdown Modal */}
      <CountdownModal isOpen={showCountdown} onComplete={handleCountdownComplete} horse={currentHorse} data-id="element-307" />
    </div>;
};